import { IconArrowRight, IconPlus, IconTrash } from '@developerz/ui';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { KeyCapacityCards } from '../lib/key-capacity';
import type { AddKeyInput, KeyRow, ProbeModelsInput, ProviderModelOption } from '../lib/keys';
import type { LogSinkStatus } from '../lib/log-sink';
import { orgHref } from '../lib/org';
import type { ToastState } from '../lib/toast';
import { AddKeyModal } from './AddKeyModal';
import { CardGridSkeleton } from './CardGridSkeleton';
import { ConfirmDialog } from './ConfirmDialog';
import { EmptyState } from './EmptyState';
import { KeyCard, providerLabel } from './KeyCard';
import { LogSinkCard } from './LogSinkCard';
import { OrgKeyGateNotice } from './OrgKeyGateNotice';
import { PageHeader } from './PageHeader';
import { Panel } from './Panel';
import { QueryBoundary, type QueryBoundaryProps } from './QueryBoundary';
import { RotateKeyModal } from './RotateKeyModal';
import { Toast } from './Toast';
import { Button, buttonClass } from './ui/Button';

// Presenter for the org BYOK route — pure rendering given the container's state
// + handlers. All data-fetching / mutation logic lives in the `AccountKeys`
// route container (two files, two reasons to change; SOLID rule).
export type AccountKeysViewProps = {
  hasSession: boolean;
  /**
   * This account holds personal keys and no ORG key — `validOrgKey` requires
   * `user_id IS NULL`, so it has zero usable keys while both lists look fine.
   * False whenever either pool's read has not landed (`lib/org-key-gate.ts`).
   */
  personalKeysOnly: boolean;
  headingRef: (el: HTMLHeadingElement) => void;
  keysQuery: QueryBoundaryProps<readonly KeyRow[]>['query'];
  // add
  addOpen: boolean;
  addBusy: boolean;
  addError: string | null;
  onOpenAdd: () => void;
  onSubmitAdd: (input: AddKeyInput) => void;
  onCloseAdd: () => void;
  /** Pre-capture enumeration for the ORG pool (`POST /v1/billing/providers/models`). */
  onProbeModels: (input: ProbeModelsInput) => Promise<readonly ProviderModelOption[]>;
  // re-verify a key against its provider (writes no onboarding state, #4613)
  onVerify: (row: KeyRow) => void;
  // revoke confirm
  onAskRevoke: (row: KeyRow) => void;
  revoking: KeyRow | null;
  onCloseRevoke: () => void;
  onConfirmRevoke: () => void;
  // rotate the raw secret in place (#971)
  onAskRotate: (row: KeyRow) => void;
  rotating: KeyRow | null;
  rotateBusy: boolean;
  rotateError: string | null;
  onSubmitRotate: (input: { readonly key: string }) => void;
  onCloseRotate: () => void;
  // re-rank a key in the pool (plan 102 slice 05b); absent = not offered
  onSetPriority?: ((row: KeyRow, priority: number) => void) | undefined;
  priorityBusy: (id: string) => boolean;
  // "Jobs at once" (#6863): every card reads its capacity line; no `onSave` = read-only
  capacity: KeyCapacityCards;
  // BYO log sink (#448)
  sinkStatus: LogSinkStatus | undefined;
  sinkBusy: boolean;
  sinkError: string | null;
  onSaveSink: (url: string) => void;
  onClearSink: () => void;
  // transient notices (add success / revoke failure)
  toast: ToastState | null;
  onToastDismiss: () => void;
};

export function AccountKeysView(props: AccountKeysViewProps): JSX.Element {
  return (
    <>
      <PageHeader
        title={t('account_keys.title')}
        desc={t('account_keys.subtitle')}
        titleRef={props.headingRef}
        actions={
          <Show when={props.hasSession}>
            <Button onClick={props.onOpenAdd}>
              <IconPlus />
              {t('account_keys.add_key')}
            </Button>
          </Show>
        }
      />

      <Show
        when={props.hasSession}
        fallback={<EmptyState message={t('account_keys.no_session')} />}
      >
        {/* Above the list, because the list is what looks empty-but-fine: the
            org pool reads "no keys yet" while `/account/my-keys` reads "valid",
            and neither said the account cannot run anything. */}
        <Show when={props.personalKeysOnly}>
          <OrgKeyGateNotice message={t('account_keys.personal_only')} />
        </Show>

        <QueryBoundary
          query={props.keysQuery}
          isEmpty={(rows) => rows.length === 0}
          loading={<CardGridSkeleton listClass="grid-cards-3 account-keys__list" />}
          empty={<EmptyState message={t('account_keys.empty')} />}
        >
          {(rows) => (
            <ul class="grid-cards-3 account-keys__list">
              <For each={rows}>
                {(row) => (
                  <KeyCard
                    row={row}
                    onRevoke={props.onAskRevoke}
                    onVerify={props.onVerify}
                    onRotate={props.onAskRotate}
                    onSetPriority={props.onSetPriority}
                    priorityBusy={props.priorityBusy(row.id)}
                    capacityRead={props.capacity.state(row.id)}
                    onSetCapacity={props.capacity.onSave}
                    capacityBusy={props.capacity.isBusy(row.id)}
                  />
                )}
              </For>
            </ul>
          )}
        </QueryBoundary>

        <LogSinkCard
          status={props.sinkStatus}
          busy={props.sinkBusy}
          error={props.sinkError}
          onSave={props.onSaveSink}
          onClear={props.onClearSink}
        />

        {/* Model selection lives on the Agents page (epic #5200): every purpose's
            model pin, thinking depth, key and provider limit sits on that
            agent's card (or its Advanced drawer / Per-repo section). This page
            goes back to key management and points at it. */}
        <Panel
          title={t('account_keys.model_selection_pointer_title')}
          class="account-keys__model-pointer"
          action={
            <a
              class={buttonClass({ variant: 'outline', size: 'sm' })}
              href={orgHref('/fleet/agents')}
            >
              {t('account_keys.model_selection_pointer_cta')}
              <IconArrowRight size="0.875rem" />
            </a>
          }
        />

        <AddKeyModal
          open={props.addOpen}
          busy={props.addBusy}
          error={props.addError}
          probe={props.onProbeModels}
          onSubmit={props.onSubmitAdd}
          onClose={props.onCloseAdd}
        />

        <RotateKeyModal
          open={props.rotating !== null}
          provider={providerLabel(props.rotating?.provider ?? '')}
          busy={props.rotateBusy}
          error={props.rotateError}
          onSubmit={props.onSubmitRotate}
          onClose={props.onCloseRotate}
        />

        <ConfirmDialog
          open={props.revoking !== null}
          title={t('account_keys.revoke_title')}
          desc={t('account_keys.revoke_body', {
            provider: providerLabel(props.revoking?.provider ?? ''),
          })}
          media={<IconTrash />}
          cancelLabel={t('account_keys.cancel')}
          actionLabel={t('account_keys.revoke')}
          destructive
          onCancel={props.onCloseRevoke}
          onConfirm={props.onConfirmRevoke}
        />
      </Show>

      <Toast
        message={props.toast?.message ?? null}
        tone={props.toast?.tone ?? 'success'}
        action={props.toast?.action}
        onDismiss={props.onToastDismiss}
      />
    </>
  );
}
